import React, { useState } from 'react';
import { Row, Col, Form, Alert, Badge, Button, ListGroup } from 'react-bootstrap';
import { FaSearch, FaCheck } from 'react-icons/fa';

const ExistingStudentsSelection = ({ 
  workflowData, 
  setWorkflowData, 
  students, 
  classes, 
  error, 
  loading, 
  gradeLevels 
}) => {
  const [studentAcademicYearFilter, setStudentAcademicYearFilter] = useState("all");
  const [studentGradeFilter, setStudentGradeFilter] = useState("all");
  const [studentClassFilter, setStudentClassFilter] = useState("all");
  const [studentSearchTerm, setStudentSearchTerm] = useState("");

  // Academic years for filtering
 const academicYears = [
    { value: "all", label: "All Academic Years" },
    { value: "2027/28", label: "2027/28 Academic Year" },
    { value: "2026/27", label: "2026/27 Academic Year" },
    { value: "2025/26", label: "2025/26 Academic Year" },
    { value: "2024/25", label: "2024/25 Academic Year" },
    { value: "2023/24", label: "2023/24 Academic Year" },
    { value: "2022/23", label: "2022/23 Academic Year" },
    { value: "older", label: "Older School Years" }
  ];
  
  const getFilteredClasses = () => {
    return classes.filter(classItem => {
      const matchesAcademicYear = studentAcademicYearFilter === "all" || classItem.academicYear === studentAcademicYearFilter;
      const matchesGrade = studentGradeFilter === "all" || classItem.gradeBoolean === studentGradeFilter;
      return matchesAcademicYear && matchesGrade;
    });
  };

  const getFilteredStudents = () => {
    const filtered = students.filter(student => {
      // First get the student's class to check academic year
      const studentClass = classes.find(c => c.id === student.classId);
      const studentAcademicYear = studentClass ? studentClass.academicYear : 'unknown';
      
      const matchesAcademicYear = studentAcademicYearFilter === "all" || studentAcademicYear === studentAcademicYearFilter;
      const matchesGrade = studentGradeFilter === "all" || student.gradeBoolean === studentGradeFilter;
      const matchesClass = studentClassFilter === "all" || student.classId === parseInt(studentClassFilter);
      const matchesSearch = student.name?.toLowerCase().includes(studentSearchTerm.toLowerCase()) ||
                           student.firstName?.toLowerCase().includes(studentSearchTerm.toLowerCase()) ||
                           student.lastName?.toLowerCase().includes(studentSearchTerm.toLowerCase()) ||
                           student.className?.toLowerCase().includes(studentSearchTerm.toLowerCase());
      
      return matchesAcademicYear && matchesGrade && matchesClass && matchesSearch;
    });
    
    return filtered;
  };

  const handleGradeFilterChange = (gradeValue) => {
    setStudentGradeFilter(gradeValue);
    if (gradeValue !== "all") {
      setStudentClassFilter("all");
    }
  };

  const handleAcademicYearFilterChange = (yearValue) => {
    setStudentAcademicYearFilter(yearValue);
  };

  const handleStudentSelect = (student) => {
    setWorkflowData(prev => {
      const selectedStudents = prev.selectedStudents || [];
      const isSelected = selectedStudents.find(s => s.id === student.id);
      if (isSelected) {
        return {
          ...prev,
          selectedStudents: selectedStudents.filter(s => s.id !== student.id)
        };
      } else {
        return {
          ...prev,
          selectedStudents: [...selectedStudents, student]
        };
      }
    });
  };

  const handleSelectAllStudents = () => {
    const filteredStudents = getFilteredStudents();
    const selectedStudents = workflowData.selectedStudents || [];
    
    if (selectedStudents.length === filteredStudents.length) {
      setWorkflowData(prev => ({ ...prev, selectedStudents: [] }));
    } else {
      setWorkflowData(prev => ({ ...prev, selectedStudents: filteredStudents }));
    }
  };

  const selectedStudents = workflowData.selectedStudents || [];
  const filteredStudents = getFilteredStudents();
  const filteredClasses = getFilteredClasses();

  return (
    <div>
      <h5>Select Existing Students</h5>
      <p className="text-muted">Choose students from existing classes to move.</p>
      
      {error && <Alert variant="danger">{error}</Alert>}

      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0">
          Students 
          <Badge bg="primary" className="ms-2">{selectedStudents.length} selected</Badge>
        </h6>
        <Button 
          variant="outline-primary" 
          size="sm" 
          onClick={handleSelectAllStudents}
          disabled={filteredStudents.length === 0}
        >
          {selectedStudents.length === filteredStudents.length ? "Deselect All" : "Select All"}
        </Button>
      </div>

      {/* Student Filters */}
      <Row className="mb-3">
        <Col md={3}>
          <Form.Select 
            value={studentAcademicYearFilter} 
            onChange={(e) => handleAcademicYearFilterChange(e.target.value)}
            size="sm"
          >
            {academicYears.map(year => (
              <option key={year.value} value={year.value}>
                {year.label}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={3}>
          <Form.Select 
            value={studentGradeFilter} 
            onChange={(e) => handleGradeFilterChange(e.target.value)}
            size="sm"
          >
            {gradeLevels.map(grade => (
              <option key={grade.value} value={grade.value}>
                {grade.label}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={3}>
          <Form.Select 
            value={studentClassFilter} 
            onChange={(e) => setStudentClassFilter(e.target.value)}
            size="sm"
          >
            <option value="all">All Classes</option>
            {filteredClasses.map(classItem => (
              <option key={classItem.id} value={classItem.id}>
                {classItem.name} ({classItem.grade})
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={3}>
          <div className="position-relative">
            <Form.Control
              type="text"
              placeholder="Search students, classes..."
              value={studentSearchTerm}
              onChange={(e) => setStudentSearchTerm(e.target.value)}
              size="sm"
            />
            <FaSearch className="position-absolute top-50 end-0 translate-middle-y me-2 text-muted" />
          </div>
        </Col>
      </Row>

      {/* Students List */}
      <div style={{ height: '400px', overflowY: 'auto' }}>
        {loading ? (
          <div className="text-center py-4">Loading students...</div>
        ) : filteredStudents.length === 0 ? (
          <div className="text-center py-4 text-muted">No students found matching filters</div>
        ) : (
          <ListGroup>
            {filteredStudents.map(student => (
              <ListGroup.Item
                key={student.id}
                className={`d-flex justify-content-between align-items-center cursor-pointer ${
                  selectedStudents.find(s => s.id === student.id) ? 'bg-light border-primary' : ''
                }`}
                onClick={() => handleStudentSelect(student)}
                style={{ cursor: 'pointer' }}
              >
                <div>
                  <div className="fw-medium">
                    {student.firstName && student.lastName 
                      ? `${student.firstName} ${student.lastName}` 
                      : student.name}
                  </div>
                  <small className="text-muted">
                    Current Class: {student.className} ({student.grade})
                  </small>
                </div>
                {selectedStudents.find(s => s.id === student.id) && (
                  <FaCheck className="text-primary" />
                )}
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </div>
    </div>
  );
};

export default ExistingStudentsSelection;